import React, { useState, useRef, useEffect } from 'react';

/**
 * SplitButton - A button with a dropdown menu for additional actions.
 * Main area triggers the primary action, chevron area toggles a dropdown.
 */
export default function SplitButton({
  label,
  onClick,
  disabled = false,
  className = '',
  icon,
  loading = false,
  loadingText,
  menuItems = [],
  type = 'button',
  title
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Extract base color classes for the chevron hover darkening
  // e.g. "bg-gray-600" -> we'll darken the chevron side on hover
  const baseClasses = className
    .split(' ')
    .filter(c => !c.startsWith('rounded') && !c.startsWith('px-') && !c.startsWith('py-'))
    .join(' ');

  return (
    <div className="relative inline-flex" ref={ref}>
      {/* Main button */}
      <button
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        className={`flex items-center space-x-2 rounded-l-md border-r border-white/20 ${className}`}
        title={title}
      >
        {loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
        ) : (
          icon
        )}
        <span>{loading && loadingText ? loadingText : label}</span>
      </button>

      {/* Chevron button */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(!open);
        }}
        disabled={disabled || loading}
        className={`flex items-center px-2 rounded-r-md hover:brightness-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${baseClasses}`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1">
          {menuItems.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setOpen(false);
                if (item.onClick) item.onClick();
              }}
              disabled={item.disabled}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
              {typeof item.label === 'string' ? <span>{item.label}</span> : item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
